import React, { type FC, useState, type PropsWithChildren } from 'react';

import { graphql, useStaticQuery } from 'gatsby';
import { Box, Icon, Link, Text } from '@lucid/components-react';
import { getProcessedValue } from '@lucid/utils';
import reactHTMLParser from 'html-react-parser';
import { type Drupal_ParagraphFlexiJobs, type GreenhouseJsonEdge } from 'generated/drupal-graphql';
import type { Lucid } from 'types/types';
import FlourishedHeading from 'components/flourished-heading/flourished-heading';
import CareerCategory from 'components/career-category/career-category';

interface FlexiJobProps {
  entity: Drupal_ParagraphFlexiJobs;
}

const PreArrow = () => (
  <span
    css={(theme: Lucid.Theme) => ({
      color: theme.colors.coolGrey[100],
      position: 'relative',
      left: 0,
      top: 'calc(50% - 9px)',
      marginRight: theme.space[2],
      fontSize: '18px',
    })}
  >
    <Icon type="ArrowRight" />
  </span>
);

const FlexiJobs: FC<PropsWithChildren<FlexiJobProps>> = ({
  entity: { fieldFlexiFormattedHeader, fieldFlexiBody, fieldAfterJobBlurb, fieldAfterSectionBlurb },
}) => {
  const allLocations = 'All Locations'; // ToDo: i18n
  const [activeLocation, setActiveLocation] = useState(allLocations);
  const queries = useStaticQuery(
    graphql`
      {
        internships: allGreenhouseJson(filter: { employmentType: { eq: "Intern" } }) {
          group(field: { primaryDepartment: { name: SELECT } }) {
            fieldValue
            totalCount
            edges {
              node {
                id
                title
                description
                location
                departments {
                  id
                  name
                }
                employmentType
                url
              }
            }
          }
        }
        teamsWithPositions: allGreenhouseJson {
          group(field: { primaryDepartment: { name: SELECT } }) {
            fieldValue
            totalCount
            edges {
              node {
                id
                title
                description
                location
                departments {
                  id
                  name
                }
                employmentType
                url
              }
            }
          }
        }
        locationsQuery: allGreenhouseJson {
          distinct(field: { location: SELECT })
        }
      }
    `,
  );

  const internshipEdges = queries.internships.group.flatMap((group: any) => group.edges);
  const internships = {
    fieldValue: 'Internships',
    edges: internshipEdges,
    totalCount: internshipEdges.length,
  };
  const teams = [...queries.teamsWithPositions.group, internships];
  const locations = queries.locationsQuery.distinct;

  const filteredTeams = teams.map((team: any) => {
    if (activeLocation !== allLocations) {
      team.filteredEdges = team.edges.filter((edge: GreenhouseJsonEdge) => edge.node?.location === activeLocation);
    } else {
      team.filteredEdges = team.edges;
    }

    return team;
  });

  return (
    <Box css={{ label: 'flexiJobs' }} gridColumn="1 / -1" id="open-positions" mb={{ default: 5, tablet: 10 }}>
      <FlourishedHeading variant="headline2XL">
        {reactHTMLParser(getProcessedValue(fieldFlexiFormattedHeader))}
      </FlourishedHeading>
      <Text as="div" css={{ maxWidth: '580px' }} variant="paragraphS400">
        {reactHTMLParser(getProcessedValue(fieldFlexiBody))}
      </Text>
      <Box mt={4}>
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent={{ default: 'center', tablet: 'initial' }}
          mb={{ default: 4, tablet: 6 }}
          mx={-2}
        >
          {[allLocations, ...locations].map((location: string) => (
            <Box key={location} display="flex" ml={2} mr={4} my={3} position="relative">
              <PreArrow />
              <Link
                css={(theme: Lucid.Theme) => ({
                  fontWeight: theme.fontWeights.strong,
                })}
                onClick={() => setActiveLocation(location)}
                tabIndex={0}
                variant={activeLocation === location ? 'primary' : 'primaryAlternate'}
              >
                {location}
              </Link>
            </Box>
          ))}
        </Box>
        <Box>
          {filteredTeams
            .filter((team: any) => team.filteredEdges.length)
            .map((team: any) => (
              <CareerCategory
                key={team.fieldValue}
                blurb={reactHTMLParser(getProcessedValue(fieldAfterJobBlurb))}
                category={team.fieldValue}
                jobs={team.filteredEdges}
              />
            ))}
        </Box>
        {fieldAfterSectionBlurb?.processed && (
          <Text
            as="div"
            css={(theme: Lucid.Theme) => ({
              marginTop: theme.space.s24,
              color: theme.colors.coolGrey[80],
              a: {
                color: theme.colors.coolGrey[80],
                fontWeight: theme.fontWeights.strong,
              },
            })}
            textAlign="right"
            variant="paragraphXS400"
          >
            {reactHTMLParser(getProcessedValue(fieldAfterSectionBlurb))}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default FlexiJobs;

export const query = graphql`
  fragment flexiJobs on Drupal_ParagraphFlexiJobs {
    fieldFlexiFormattedHeader {
      processed
    }
    fieldFlexiBody {
      processed
    }
    fieldAfterJobBlurb {
      processed
    }
    fieldAfterSectionBlurb {
      processed
    }
  }
`;
